// EditSectionModal.jsx
import React, { useEffect, useState } from "react";
import { X, Check } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import CustomEditor from "../common/Editor";

export default function EditSectionModal({
  isOpen,
  onClose,
  section,
  index,
  onUpdate,
}) {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language?.includes("fa");

  const toStr = (v) => {
    if (v == null) return "";
    if (typeof v === "string") return v;
    if (typeof v === "number") return String(v);
    if (typeof v === "object") {
      if (typeof v.fa === "string") return v.fa;
      if (typeof v.en === "string") return v.en;
      if (v.fa && typeof v.fa.fa === "string") return v.fa.fa;
      if (v.fa && typeof v.fa.en === "string") return v.fa.en;
      if (v.en && typeof v.en.fa === "string") return v.en.fa;
      if (v.en && typeof v.en.en === "string") return v.en.en;
    }
    return "";
  };

  const pick = (obj, key) => toStr(obj?.[key]);

  const [form, setForm] = useState({
    type: "classic", // ✅
    title_fa: "",
    title_en: "",
    description_fa: "",
    description_en: "",
    timeLimitSec: "",
    shuffleItems: false,
    audioFile: { url: "", maxPlays: 1, allowPause: false },
    displayMode: "one_per_page",

    // ✅ AI config
    ai: {
      mode: "text", // text | voice
      count: 10,
      points: 10,
      difficulty: "medium", // easy|medium|hard
      language: "auto", // auto|fa|en
    },
  });

  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!section) return;

    const type = section?.type === "ai" ? "ai" : "classic";

    setForm({
      type,
      title_fa: pick(section.title, "fa"),
      title_en: pick(section.title, "en"),
      description_fa: pick(section.description, "fa"),
      description_en: pick(section.description, "en"),
      timeLimitSec:
        typeof section.timeLimitSec === "number" ? section.timeLimitSec : "",
      shuffleItems: !!section.shuffleItems,
      audioFile: {
        url: toStr(section.audioFile?.url),
        maxPlays:
          typeof section.audioFile?.maxPlays === "number"
            ? section.audioFile.maxPlays
            : 1,
        allowPause: !!section.audioFile?.allowPause,
      },
      displayMode: section.displayMode || "one_per_page",

      ai: {
        mode: section?.ai?.mode === "voice" ? "voice" : "text",
        count: typeof section?.ai?.count === "number" ? section.ai.count : 10,
        points: typeof section?.ai?.points === "number" ? section.ai.points : 1,
        difficulty: section?.ai?.difficulty || "medium",

        language: section?.ai?.language || "auto",
      },
    });
  }, [section]);

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
  };

  const handleEditorChange = (field, content) => {
    setForm((f) => ({ ...f, [field]: content }));
  };

  const handleAudioChange = (field, value) => {
    setForm((f) => ({ ...f, audioFile: { ...f.audioFile, [field]: value } }));
  };

  const setAi = (patch) =>
    setForm((f) => ({ ...f, ai: { ...f.ai, ...patch } }));

  const handleTypeChange = (nextType) => {
    setForm((f) => {
      if (nextType === "ai") {
        return {
          ...f,
          type: "ai",
          // classic options بی‌اثر
          displayMode: "one_per_page",
          audioFile: { url: "", maxPlays: 1, allowPause: false },
          shuffleItems: false,
        };
      }
      return { ...f, type: "classic" };
    });
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();

    const titleFa = toStr(form.title_fa).trim();
    const titleEn = toStr(form.title_en).trim();
    if (!titleFa && !titleEn) {
      toast.error(t("exam_builder.toasts.section_title_required"));
      return;
    }

    setSaving(true);
    try {
      const basePayload = {
        type: form.type, // ✅
        title: { fa: titleFa, en: titleEn },
        description: {
          fa: toStr(form.description_fa),
          en: toStr(form.description_en),
        },
        timeLimitSec: form.timeLimitSec ? Number(form.timeLimitSec) : undefined,
        meta: section?.meta, // layout رو دست نزنیم
      };

      let payload;

      if (form.type === "ai") {
        payload = {
          ...basePayload,
          ai: {
            mode: form.ai.mode,
            count: Number(form.ai.count || 10),
            points: form.ai.points,
            difficulty: form.ai.difficulty,
            language: form.ai.language || "auto",
          },
          // ✅ مهم: AI items/parts دستی نداره
          items: [],
          parts: [],
          shuffleItems: false,
          displayMode: "one_per_page",
          audioFile: undefined,
        };
      } else {
        payload = {
          ...basePayload,
          shuffleItems: !!form.shuffleItems,
          displayMode: form.displayMode,
          audioFile:
            form.displayMode === "all_at_once" && toStr(form.audioFile.url)
              ? {
                  url: toStr(form.audioFile.url),
                  maxPlays:
                    typeof form.audioFile.maxPlays === "number"
                      ? form.audioFile.maxPlays
                      : 1,
                  allowPause: !!form.audioFile.allowPause,
                }
              : undefined,
          items: Array.isArray(section?.items) ? section.items : [],
          // ai را پاک کن (اختیاری)
          ai: undefined,
        };
      }

      onUpdate?.(payload, index);
      onClose?.();
    } catch (err) {
      toast.error(err?.message || t("common.unknown_error"));
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen || !section) return null;

  const isAi = form.type === "ai";

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white w-full max-w-2xl rounded-2xl shadow-xl p-4 md:p-6 max-h-[80vh] overflow-y-scroll"
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg md:text-xl font-semibold">
            {t("exam_builder.edit_section")}
          </h2>
          <button
            className="p-2 rounded-lg hover:bg-gray-100"
            onClick={onClose}
            aria-label="close"
          >
            <X />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* ✅ نوع سکشن */}
          <div className="border rounded-lg p-3 bg-gray-50">
            <label className="block text-sm font-medium mb-1">نوع سکشن</label>
            <select
              className="w-full border rounded-lg px-3 py-2 bg-white"
              value={form.type}
              onChange={(e) => handleTypeChange(e.target.value)}
              // اگر نمی‌خوای قابل تغییر باشه:
              // disabled
            >
              <option value="classic">کلاسیک (سوالات آماده)</option>
              <option value="ai">هوش مصنوعی (سوال‌به‌سوال)</option>
            </select>

            {isAi && (
              <p className="text-xs text-gray-500 mt-2">
                در سکشن هوش مصنوعی، سوالات در زمان اجرای آزمون تولید و تصحیح
                می‌شوند و مدیریت دستی سوالات وجود ندارد.
              </p>
            )}
          </div>

          {/* Title */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div dir={isRtl ? "rtl" : "ltr"}>
              <label className="block text-sm font-medium mb-1">
                {t("section_fields.title_fa")}
              </label>
              <input
                className="w-full border rounded-lg px-3 py-2"
                name="title_fa"
                value={form.title_fa}
                onChange={onChange}
                placeholder={t("section_fields.title_ph")}
              />
            </div>
            <div dir="ltr">
              <label className="block text-sm font-medium mb-1">
                {t("section_fields.title_en")}
              </label>
              <input
                className="w-full border rounded-lg px-3 py-2"
                name="title_en"
                value={form.title_en}
                onChange={onChange}
                placeholder={t("section_fields.title_ph")}
              />
            </div>
          </div>

          {/* Description */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div dir={isRtl ? "rtl" : "ltr"}>
              <label className="block text-sm font-medium mb-1">
                {t("section_fields.description_fa")}
              </label>
              <CustomEditor
                value={form.description_fa}
                onEditorChange={(content) =>
                  handleEditorChange("description_fa", content)
                }
                placeholder={t("section_fields.description_ph")}
                directionality={isRtl ? "rtl" : "ltr"}
                height={150}
                toolbar="undo redo | bold italic underline | bullist numlist"
              />
            </div>
            <div dir="ltr">
              <label className="block text-sm font-medium mb-1">
                {t("section_fields.description_en")}
              </label>
              <CustomEditor
                value={form.description_en}
                onEditorChange={(content) =>
                  handleEditorChange("description_en", content)
                }
                placeholder={t("section_fields.description_ph")}
                directionality="ltr"
                height={150}
                toolbar="undo redo | bold italic underline | bullist numlist"
              />
            </div>
          </div>

          {/* ✅ AI Fields */}
          {isAi && (
            <div className="space-y-4 border p-4 rounded-lg bg-indigo-50">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-1">حالت</label>
                  <select
                    className="w-full border rounded-lg px-3 py-2 bg-white"
                    value={form.ai.mode}
                    onChange={(e) => setAi({ mode: e.target.value })}
                  >
                    <option value="text">متنی (چت)</option>
                    <option value="voice">گویشی (صوت)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">
                    نمره این سکشن
                  </label>
                  <input
                    type="number"
                    min={0}
                    className="w-full border rounded-lg px-3 py-2"
                    name="points"
                    value={form.ai.points}
                    onChange={(e) =>
                      setAi({
                        points: Number(e.target.value || 0),
                      })
                    }
                    placeholder="مثلاً 10"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    هوش مصنوعی در پایان این سکشن از ۰ تا این نمره امتیاز می‌دهد.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">
                    تعداد سوال
                  </label>
                  <input
                    type="number"
                    min={1}
                    className="w-full border rounded-lg px-3 py-2 bg-white"
                    value={form.ai.count}
                    onChange={(e) =>
                      setAi({ count: Number(e.target.value || 10) })
                    }
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">سختی</label>
                  <select
                    className="w-full border rounded-lg px-3 py-2 bg-white"
                    value={form.ai.difficulty}
                    onChange={(e) => setAi({ difficulty: e.target.value })}
                  >
                    <option value="easy">آسان</option>
                    <option value="medium">متوسط</option>
                    <option value="hard">سخت</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">زبان</label>
                  <select
                    className="w-full border rounded-lg px-3 py-2 bg-white"
                    value={form.ai.language}
                    onChange={(e) => setAi({ language: e.target.value })}
                  >
                    <option value="auto">خودکار</option>
                    <option value="fa">فارسی</option>
                    <option value="en">English</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Classic-only settings */}
          {!isAi && (
            <>
              <div className="space-y-4 border p-4 rounded-lg bg-gray-50">
                <label className="block text-sm font-medium mb-1">
                  {t("section_fields.display_mode")}
                </label>
                <select
                  className="w-full border rounded-lg px-3 py-2"
                  name="displayMode"
                  value={form.displayMode}
                  onChange={onChange}
                >
                  <option value="one_per_page">
                    {t("section_fields.one_per_page")}
                  </option>
                  <option value="all_at_once">
                    {t("section_fields.all_at_once")}
                  </option>
                </select>

                {form.displayMode === "all_at_once" && (
                  <div className="space-y-4">
                    <h4 className="font-semibold text-gray-700">
                      {t("section_fields.audio_settings")}
                    </h4>
                    <div>
                      <label className="block text-sm font-medium mb-1">
                        {t("section_fields.audio_url")}
                      </label>
                      <input
                        type="text"
                        className="w-full border rounded-lg px-3 py-2"
                        name="url"
                        value={form.audioFile.url}
                        onChange={(e) =>
                          handleAudioChange("url", e.target.value)
                        }
                        placeholder={t("section_fields.audio_url_ph")}
                      />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium mb-1">
                          {t("section_fields.max_plays")}
                        </label>
                        <input
                          type="number"
                          className="w-full border rounded-lg px-3 py-2"
                          name="maxPlays"
                          value={form.audioFile.maxPlays}
                          onChange={(e) =>
                            handleAudioChange(
                              "maxPlays",
                              Number(e.target.value)
                            )
                          }
                          min={1}
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="allowPause"
                          name="allowPause"
                          checked={form.audioFile.allowPause}
                          onChange={(e) =>
                            handleAudioChange("allowPause", e.target.checked)
                          }
                          className="h-4 w-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                        />
                        <label htmlFor="allowPause" className="text-sm">
                          {t("section_fields.allow_pause")}
                        </label>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-1">
                    {t("section_fields.time_limit")}
                  </label>
                  <input
                    type="number"
                    min={0}
                    className="w-full border rounded-lg px-3 py-2"
                    name="timeLimitSec"
                    value={form.timeLimitSec}
                    onChange={onChange}
                    placeholder={t("section_fields.time_limit_ph")}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {t("section_fields.time_limit_hint")}
                  </p>
                </div>

                <div className="flex items-center gap-2 mt-4">
                  <input
                    id="shuffleItems"
                    type="checkbox"
                    name="shuffleItems"
                    checked={!!form.shuffleItems}
                    onChange={onChange}
                    className="h-4 w-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                  />
                  <label htmlFor="shuffleItems" className="text-sm">
                    {t("section_fields.shuffle_items")}
                  </label>
                </div>
              </div>
            </>
          )}

          {/* Time Limit (هم برای AI هم classic خوبه) */}
          {isAi && (
            <div>
              <label className="block text-sm font-medium mb-1">
                {t("section_fields.time_limit")}
              </label>
              <input
                type="number"
                min={0}
                className="w-full border rounded-lg px-3 py-2"
                name="timeLimitSec"
                value={form.timeLimitSec}
                onChange={onChange}
                placeholder={t("section_fields.time_limit_ph")}
              />
              <p className="text-xs text-gray-500 mt-1">
                {t("section_fields.time_limit_hint")}
              </p>
            </div>
          )}

          <div
            className={`flex ${
              isRtl ? "justify-start" : "justify-end"
            } gap-3 pt-2`}
          >
            <button
              type="button"
              className="px-4 py-2 rounded-lg border hover:bg-gray-50"
              onClick={onClose}
            >
              {t("common.cancel")}
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 rounded-lg bg-purple-600 text-white hover:bg-purple-700 inline-flex items-center gap-2"
            >
              <Check className="w-4 h-4" />
              {saving ? t("common.saving") : t("exam_builder.save_section")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
